import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Users, TrendingUp, ArrowRight, Shield, BarChart3 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { SecurityBoundary, PermissionBoundary } from '@/lib/security';
import { PermissionScope } from '@/types';
import { LastRefreshIndicator } from '@/components/LastRefreshIndicator';
import { logUserActivity } from '@/lib/activity/userActivity';
import logoEmblem from '@/assets/logos/vehix-emblem.png';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  // Check permissions for workflow steps
  const canViewRecalls = hasPermission({
    resource: 'recalls',
    action: 'read',
    scope: PermissionScope.SELF
  });
  const canAssignTechnicians = hasPermission({
    resource: 'recalls',
    action: 'assign_technicians',
    scope: PermissionScope.SELF
  });
  const canViewRoutes = hasPermission({
    resource: 'routes',
    action: 'read',
    scope: PermissionScope.SELF
  });

  const workflowSteps = [
    {
      id: 1,
      title: 'Search Recalls',
      description: 'Find and prioritize high-value recalls instantly with AI-powered search',
      icon: Search,
      color: 'from-brand-primary-500 to-brand-primary-600',
      action: () => {
        logUserActivity({
          category: 'recall_selection',
          action: 'landing_page_click',
          metadata: { button: 'search_recalls', destination: '/recall-map' }
        });
        navigate('/recall-map');
      },
      stats: '10,000+ recalls tracked',
      enabled: canViewRecalls
    },
    {
      id: 2,
      title: 'Plan & Assign',
      description:
        'Assign technicians to recalls, preview optimized routes, and generate printouts',
      icon: Users,
      color: 'from-brand-secondary-500 to-brand-secondary-600',
      action: () => {
        logUserActivity({
          category: 'routing',
          action: 'landing_page_click',
          metadata: { button: 'plan_assign', destination: '/routing/manual' }
        });
        navigate('/routing/manual');
      },
      stats: 'Route planning & printouts',
      enabled: canAssignTechnicians
    },
    {
      id: 3,
      title: 'Track Routes',
      description: 'Monitor technician routes and recall completion status',
      icon: TrendingUp,
      color: 'from-brand-accent-500 to-brand-accent-600',
      action: () => {
        logUserActivity({
          category: 'route_tracking',
          action: 'landing_page_click',
          metadata: { button: 'track_routes', destination: '/route-tracker' }
        });
        navigate('/route-tracker');
      },
      stats: 'Route progress tracking',
      enabled: canViewRoutes
    }
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  return (
    <SecurityBoundary
      requiredPermissions={[{ resource: 'dashboard', action: 'read', scope: PermissionScope.SELF }]}
      auditPageAccess='dashboard'
      tenantIsolation={true}
    >
      <div className='w-full h-screen flex flex-col overflow-hidden'>
        {/* Hero Section - Fluent UI 2-Tone Design - 1/3 of page, full width */}
        <section
          className='relative overflow-hidden transition-colors duration-200 flex-shrink-0 -mx-2 sm:-mx-4 md:-mx-6 bg-[rgb(242.76,239.7,233.835)] dark:bg-[hsl(39,39%,8%)]'
          style={{
            height: '33.333vh'
          }}
        >
          <div className='relative w-full h-full flex items-center justify-center overflow-hidden px-4'>
            {/* Emblem - Center column */}
            <div className='flex items-center justify-center'>
              <img
                src={logoEmblem}
                alt='Vehix'
                className='h-[28vh] w-[28vh] object-contain shadow-none max-w-full max-h-full'
              />
            </div>
          </div>
        </section>

        {/* Bottom Sections - 2/3 of page */}
        <div className='flex-1 overflow-y-auto mt-2'>
          {/* Workflow Section - Lighter bottom section */}
          <section className='py-3 px-4 sm:px-6 lg:px-8 bg-background'>
            <div className='mx-auto max-w-7xl'>
              <motion.div className='text-center mb-3' {...fadeInUp}>
                <h2 className='text-xl font-bold text-foreground mb-1'>
                  Your Recall Management Workflow
                </h2>
                <p className='text-sm text-muted-foreground max-w-2xl mx-auto'>
                  Three simple steps to maximize your service revenue and customer satisfaction
                </p>
              </motion.div>

              <div className='grid gap-4 md:grid-cols-3'>
                {workflowSteps.map((step, index) => (
                  <motion.div
                    key={step.id}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card
                      className={`relative h-full overflow-hidden border-0 shadow-lg transition-all duration-300 group ${
                        step.enabled
                          ? 'hover:shadow-xl cursor-pointer'
                          : 'opacity-50 cursor-not-allowed'
                      }`}
                      onClick={step.enabled ? step.action : undefined}
                    >
                      <div
                        className={`absolute inset-0 bg-gradient-to-br ${step.color} opacity-5 group-hover:opacity-10 transition-opacity`}
                      />
                      <CardContent className='p-5'>
                        <div className='mb-4 flex items-center justify-between'>
                          <div
                            className={`inline-flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br ${step.color}`}
                          >
                            <step.icon className='h-6 w-6 text-white' />
                          </div>
                          <span className='text-3xl font-bold text-muted-foreground'>
                            {step.id}
                          </span>
                        </div>

                        <h3 className='mb-2 text-lg font-semibold text-foreground'>{step.title}</h3>
                        <p className='mb-4 text-sm text-muted-foreground min-h-[40px]'>
                          {step.description}
                        </p>

                        <div className='flex items-center justify-between'>
                          <span className='text-sm font-medium text-muted-foreground'>
                            {step.stats}
                          </span>
                          <ArrowRight className='h-5 w-5 text-muted-foreground group-hover:text-foreground group-hover:translate-x-1 transition-all' />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Quick Actions Section - Lighter bottom section */}
          <section className='bg-background py-4 px-4 sm:px-6 lg:px-8'>
            <div className='mx-auto max-w-7xl'>
              <motion.div className='grid gap-4 md:grid-cols-2 lg:grid-cols-3' {...fadeInUp}>
                <PermissionBoundary
                  require={{ resource: 'campaigns', action: 'read', scope: PermissionScope.SELF }}
                  fallback={
                    <Card className='border-0 shadow-sm opacity-50 cursor-not-allowed'>
                      <CardContent className='p-5 text-center'>
                        <BarChart3 className='mx-auto mb-3 h-10 w-10 text-muted-foreground' />
                        <h4 className='font-semibold text-muted-foreground mb-1'>
                          Campaign Settings
                        </h4>
                        <p className='text-sm text-muted-foreground'>Access restricted</p>
                      </CardContent>
                    </Card>
                  }
                >
                  <Card
                    className='border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer'
                    onClick={() => {
                      logUserActivity({
                        category: 'routing',
                        action: 'landing_page_click',
                        metadata: { button: 'campaign_settings', destination: '/campaigns' }
                      });
                      navigate('/campaigns');
                    }}
                  >
                    <CardContent className='p-5 text-center'>
                      <BarChart3 className='mx-auto mb-3 h-10 w-10 text-brand-primary-500 dark:text-brand-primary-400' />
                      <h4 className='font-semibold text-foreground mb-1'>Campaign Settings</h4>
                      <p className='text-sm text-muted-foreground'>
                        Manage status, skill levels, and mobile servicing options
                      </p>
                    </CardContent>
                  </Card>
                </PermissionBoundary>

                <PermissionBoundary
                  require={{ resource: 'technicians', action: 'read', scope: PermissionScope.SELF }}
                  fallback={
                    <Card className='border-0 shadow-sm opacity-50 cursor-not-allowed'>
                      <CardContent className='p-5 text-center'>
                        <Users className='mx-auto mb-3 h-10 w-10 text-muted-foreground' />
                        <h4 className='font-semibold text-muted-foreground mb-1'>
                          Manage Technicians
                        </h4>
                        <p className='text-sm text-muted-foreground'>Access restricted</p>
                      </CardContent>
                    </Card>
                  }
                >
                  <Card
                    className='border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer'
                    onClick={() => {
                      logUserActivity({
                        category: 'routing',
                        action: 'landing_page_click',
                        metadata: { button: 'manage_technicians', destination: '/technicians' }
                      });
                      navigate('/technicians');
                    }}
                  >
                    <CardContent className='p-5 text-center'>
                      <Users className='mx-auto mb-3 h-10 w-10 text-brand-secondary-500 dark:text-brand-secondary-400' />
                      <h4 className='font-semibold text-foreground mb-1'>Manage Technicians</h4>
                      <p className='text-sm text-muted-foreground'>
                        Add, edit, and configure your technician team
                      </p>
                    </CardContent>
                  </Card>
                </PermissionBoundary>

                <PermissionBoundary
                  require={{ resource: 'dealers', action: 'read', scope: PermissionScope.SELF }}
                  fallback={
                    <Card className='border-0 shadow-sm opacity-50 cursor-not-allowed'>
                      <CardContent className='p-5 text-center'>
                        <Shield className='mx-auto mb-3 h-10 w-10 text-muted-foreground' />
                        <h4 className='font-semibold text-muted-foreground mb-1'>
                          Dealer Settings
                        </h4>
                        <p className='text-sm text-muted-foreground'>Access restricted</p>
                      </CardContent>
                    </Card>
                  }
                >
                  <Card
                    className='border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer'
                    onClick={() => {
                      logUserActivity({
                        category: 'routing',
                        action: 'landing_page_click',
                        metadata: { button: 'dealer_settings', destination: '/dealers' }
                      });
                      navigate('/dealers');
                    }}
                  >
                    <CardContent className='p-5 text-center'>
                      <Shield className='mx-auto mb-3 h-10 w-10 text-brand-accent-500 dark:text-brand-accent-400' />
                      <h4 className='font-semibold text-foreground mb-1'>Dealer Settings</h4>
                      <p className='text-sm text-muted-foreground'>
                        Manage dealers, preferences, and notes
                      </p>
                    </CardContent>
                  </Card>
                </PermissionBoundary>
              </motion.div>
            </div>
          </section>

          {/* Text Content Section - Below Quick Actions */}
          <section className='bg-background py-4 px-4 sm:px-6 lg:px-8'>
            <div className='mx-auto max-w-7xl'>
              <div className='text-center space-y-2'>
                <p className='text-sm sm:text-base text-muted-foreground leading-relaxed'>
                  Search and assign technicians for recall servicing in{' '}
                  <span className='font-semibold text-brand-primary-700 dark:text-brand-primary-400'>
                    under 5 minutes
                  </span>
                  . AI-powered recall management that transforms your dealership&apos;s service
                  operations.
                </p>
                <div className='flex justify-center'>
                  <LastRefreshIndicator type='recall-data' className='text-xs' />
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </SecurityBoundary>
  );
};

export default LandingPage;
